import * as CubiqTS from "shared/cubiq-model-multi-asset";
import { BmarkFixingType } from "./enums/BmarkFixingType.enum";
import { BmarkFixingTileColor } from "./enums/BmarkFixingTileColor.enum";

interface BmarkFillCount {
  filled: number;
  fillError: number;
  fillExpired: number;
  fwdPending: number;
  pending: number;
}

interface BmarkFillStatusState {
  orderState: CubiqTS.OrderState;
  orderType: CubiqTS.OrderType;
  bmOrderType: CubiqTS.BenchmarkOrderType;
  isProcessesFillUpdatesAfterCancellationEnabled: boolean;
  type: BmarkBlotterType | null;
  // WARNING:
  // don't store of record - this changes dynamically as it moves
  // fixingType: BmarkFixingType,
}

enum BmarkBlotterType {
  UNKNOWN,
  CLIENT_ORDER,
  HEDGE_ORDER,
}

export class BmarkFillStatusMessageData {
  // these are seperate for merge&move related reasons
  // counts and ultimately the message are shared
  private clientOrdersStateMap: Map<string, BmarkFillStatusState> = new Map();
  private hedgeOrdersStateMap: Map<string, BmarkFillStatusState> = new Map();

  constructor() {}

  public static merge(
    current: BmarkFillStatusMessageData,
    old: BmarkFillStatusMessageData,
  ): BmarkFillStatusMessageData {
    let merged = new BmarkFillStatusMessageData();
    merged.clientOrdersStateMap = new Map([
      ...current.clientOrdersStateMap,
      ...old.clientOrdersStateMap,
    ]);
    merged.hedgeOrdersStateMap = new Map([
      ...current.hedgeOrdersStateMap,
      ...old.hedgeOrdersStateMap,
    ]);
    return merged;
  }

  public replaceClientOrders(source: BmarkFillStatusMessageData): BmarkFillStatusMessageData {
    this.clientOrdersStateMap = source.clientOrdersStateMap;
    return this;
  }

  public replaceHedgeOrders(source: BmarkFillStatusMessageData): BmarkFillStatusMessageData {
    this.hedgeOrdersStateMap = source.hedgeOrdersStateMap;
    return this;
  }

  public update(orderData: CubiqTS.OrderData, traderGroup: CubiqTS.TradingGroupImpl) {
    this.remove(orderData);
    let orderId = orderData.orderId.toString();

    let state = {
      orderState: orderData.orderState,
      orderType: orderData.orderType,
      bmOrderType: orderData.fixingDetails != null ? orderData.fixingDetails.bmOrderType : null,
      isProcessesFillUpdatesAfterCancellationEnabled:
        traderGroup != null ? traderGroup.isProcessesFillUpdatesAfterCancellationEnabled : true,
      type: this.getBmarkBlotterType(orderData),
    };

    if (BmarkBlotterType.CLIENT_ORDER === state.type) {
      this.clientOrdersStateMap.set(orderId, state);
    } else if (BmarkBlotterType.HEDGE_ORDER === state.type) {
      this.hedgeOrdersStateMap.set(orderId, state);
    }
  }

  private countState(
    fixingType: BmarkFixingType,
    state: BmarkFillStatusState,
    count: BmarkFillCount,
  ) {
    if (
      CubiqTS.OrderState.FILL_ERROR === state.orderState ||
      (BmarkFixingType.RECENT == fixingType &&
        (CubiqTS.OrderState.REJECTED === state.orderState ||
          CubiqTS.OrderState.ABORTED === state.orderState ||
          CubiqTS.OrderState.TIMED_OUT === state.orderState ||
          (state.isProcessesFillUpdatesAfterCancellationEnabled &&
            // CANCELLED in BMHO's is non terminal. waiting on rate
            CubiqTS.OrderType.BENCHMARK === state.orderType &&
            CubiqTS.BenchmarkOrderType.PositionOrder === state.bmOrderType &&
            CubiqTS.OrderState.CANCELLED === state.orderState)))
    ) {
      count.fillError++;
    } else if (CubiqTS.OrderState.FILL_EXPIRED === state.orderState) {
      count.fillExpired++;
    } else if (CubiqTS.OrderState.PENDING_FWDS === state.orderState) {
      count.fwdPending++;
    } else if (
      CubiqTS.OrderState.FILLED === state.orderState ||
      CubiqTS.OrderState.CANCELLED === state.orderState ||
      CubiqTS.OrderState.EXPIRED === state.orderState
    ) {
      count.filled++;
    } else {
      count.pending++;
    }

    return count;
  }

  private getBmarkBlotterType(orderData): BmarkBlotterType {
    if (
      CubiqTS.OrderType.BENCHMARK === orderData.orderType &&
      CubiqTS.BenchmarkOrderType.PositionOrder !== orderData.fixingDetails.bmOrderType
    ) {
      return BmarkBlotterType.CLIENT_ORDER;
    } else if (
      CubiqTS.OrderType.BMALGO === orderData.orderType ||
      (CubiqTS.OrderType.BENCHMARK === orderData.orderType &&
        CubiqTS.BenchmarkOrderType.PositionOrder === orderData.fixingDetails.bmOrderType)
    ) {
      return BmarkBlotterType.HEDGE_ORDER;
    }
  }

  public remove(orderData: CubiqTS.OrderDataImpl | string) {
    let orderId: string =
      orderData instanceof CubiqTS.OrderDataImpl ? orderData.orderId.toString() : orderData;
    this.clientOrdersStateMap.delete(orderId);
    this.hedgeOrdersStateMap.delete(orderId);
  }

  public i18nFillStatusMessage(
    fixingType: BmarkFixingType,
    fixingState: CubiqTS.FixingState,
  ): string {
    let count: BmarkFillCount = {
      filled: 0,
      fillError: 0,
      fillExpired: 0,
      pending: 0,
      fwdPending: 0,
    };

    // traverse both maps by iterator
    for (let state of this.clientOrdersStateMap.values()) {
      count = this.countState(fixingType, state, count);
    }

    for (let state of this.hedgeOrdersStateMap.values()) {
      count = this.countState(fixingType, state, count);
    }

    if (count.fillError > 0 || count.fillExpired > 0) {
      return "benchmark.summary.fixing.status.pricing.error";
    } else if (count.fwdPending > 0 && count.pending == 0) {
      return "benchmark.summary.fixing.status.fwdpending";
    } else if (count.filled > 0 && count.pending == 0) {
      return "benchmark.summary.fixing.status.filled";
    } else if (
      BmarkFixingType.RECENT == fixingType ||
      // even pre-sweep no longer considered active when fixing time is hit
      (BmarkFixingType.ACTIVE === fixingType &&
        (CubiqTS.FixingState.COMPLETED === fixingState ||
          CubiqTS.FixingState.FIXING_HIT === fixingState ||
          CubiqTS.FixingState.TRIGGERED === fixingState))
    ) {
      return "benchmark.summary.fixing.status.reached";
    } else {
      return "benchmark.summary.fixing.status.active";
    }
  }

  public getDisplayColor(
    fixingType: BmarkFixingType,
    fixingState: CubiqTS.FixingState,
    fixingTimestamp: number,
  ): BmarkFixingTileColor {
    let result: BmarkFixingTileColor;
    if (fixingType != null && typeof fixingTimestamp == "number") {
      let orderDataPopulated: boolean =
        this.clientOrdersStateMap.size > 0 || this.hedgeOrdersStateMap.size > 0;
      // orderState
      let count: BmarkFillCount = {
        filled: 0,
        fillError: 0,
        fillExpired: 0,
        pending: 0,
        fwdPending: 0,
      };
      for (let state of this.clientOrdersStateMap.values()) {
        count = this.countState(fixingType, state, count);
      }
      for (let state of this.hedgeOrdersStateMap.values()) {
        count = this.countState(fixingType, state, count);
      }

      if (fixingType === BmarkFixingType.ACTIVE) {
        // seconds
        let fixingMoment = CubiqTS.moment(fixingTimestamp);
        let now = CubiqTS.moment();
        let deltaSeconds = fixingMoment.diff(now, "seconds");
        // days
        let fixingDay = CubiqTS.moment(fixingMoment.format("DD-MMM-YY"), "DD-MMM-YY");
        let today = CubiqTS.moment(CubiqTS.moment().format("DD-MMM-YY"), "DD-MMM-YY");
        let deltaDay = fixingDay.diff(today, "days");

        if (fixingState === CubiqTS.FixingState.HARD_CUTOFF) {
          result = BmarkFixingTileColor.HCO;
        } else if (fixingState === CubiqTS.FixingState.SOFT_CUTOFF) {
          result = BmarkFixingTileColor.SCO;
        } else if (deltaDay > 0) {
          // FUTURE
          result = BmarkFixingTileColor.FUTURE;
        } else if (deltaDay === 0) {
          // TODAY
          if (deltaSeconds >= 0) {
            // fixing time in future (but today)
            result = BmarkFixingTileColor.TODAY;
          } else if (orderDataPopulated) {
            // fixing time passed
            if (count.fillError > 0 || count.fillExpired > 0 || count.pending > 0) {
              result = BmarkFixingTileColor.ACTIVE;
            } else if (count.fwdPending > 0) {
              result = BmarkFixingTileColor.FWD_PENDING;
            } else if (count.filled > 0) {
              result = BmarkFixingTileColor.COMPLETED;
            } else {
              //logger.out.error(`ERROR: ${this.constructor.name}.determineDisplayColor(): Active Sweep time uncertainty`);
              result = BmarkFixingTileColor.ACTIVE;
            }
          } else {
            // default
            //logger.out.warn(`ERROR: ${this.constructor.name}.determineDisplayColor(): ACTIVE - No data`);
            result = BmarkFixingTileColor.TODAY;
          }
        } else {
          //logger.out.error(`ERROR: ${this.constructor.name}.determineDisplayColor(): This should not happen`);
          result = BmarkFixingTileColor.TODAY;
        }

        //logger.out.warn(`determineDisplayColor() -> Day comparison: Today: (${today.format('DD-MMM-YY')}) | Fixing: (${fixingDay.format('DD-MMM-YY')}) | Delta: '${deltaDay}' days | COLOR ${BmarkFixingTileColor[result]}`);
        //logger.out.warn(`determineDisplayColor() -> Seconds comparison: Now: (${now.format('HH:mm:ss')}) | Fixing (${fixingMoment.format('HH:mm:ss')}) | Delta: '${deltaSeconds}' seconds | COLOR ${BmarkFixingTileColor[result]}`);
      } else if (fixingType === BmarkFixingType.RECENT) {
        if (orderDataPopulated) {
          if (count.fillError > 0 || count.fillExpired > 0 || count.pending > 0) {
            result = BmarkFixingTileColor.ACTIVE;
          } else if (count.fwdPending > 0) {
            result = BmarkFixingTileColor.FWD_PENDING;
          } else if (count.filled > 0) {
            result = BmarkFixingTileColor.COMPLETED;
          } else {
            // default
            //logger.out.error(`ERROR: ${this.constructor.name}.determineDisplayColor(): Recent Sweep time uncertainty`);
            result = BmarkFixingTileColor.ACTIVE;
          }
        } else {
          // no order data available
          result = BmarkFixingTileColor.TODAY;
        }
      } else {
        throw new Error(`ERROR: determineDisplayColor(): Invalid fixingType (Result -> DEFAULT)`);
      }
    } else {
      throw new Error(`ERROR: determineDisplayColor(): Undefined fixingType or fixingMoment `);
    }
    // TODAY is used as global default (this is the color we use when there is no data)
    return result != null ? result : BmarkFixingTileColor.TODAY;
  }
}
